// imports
import { useState, useEffect, useRef, createContext } from 'react'
import { RouteObject, RouterProvider, createBrowserRouter } from 'react-router-dom'
import type { NavigateOptions } from 'react-router-dom'

// components
import BaseLayout from '@/ui/components/Layout/Base/BaseLayout'
import IdentityLayout from '@/ui/components/Layout/Identity/IdentityLayout'
import Home from '@/ui/pages/Home/Home'
import Login from '@/ui/pages/identity/Login/Login'
import AuthenticationError from '@/ui/pages/identity/AuthenticationError/AuthenticationError'
import NotFound from '@/ui/pages/NotFound/NotFound'
import ChatLoader from '@/ui/pages/Chat/ChatLoader'
import ContentCenter from '@/ui/components/Layout/Base/ContentCenter'
import StartupGate from '@/ui/components/StartupSplash/StartupGate'
import Feedback from '@/ui/pages/Feedback/Feedback'
import Settings from '@/ui/pages/Settings/Settings'

// utils
import { useSpace } from '@/hooks/useSpace'
import { useAuthentication } from '@/hooks/useAuthentication'
import useGenericStore from '@/hooks/useGenericStore'

// types
import { LastChatInfo } from '@/ui/advancedAssistants/MMA/types/MMA'

// - Context ---------------------

const NavigationContext = createContext<{
    router: ReturnType<typeof createBrowserRouter> | undefined,
    navigateTo: (path: string, options?: NavigateOptions) => void
}>({
    router: undefined,
    navigateTo: () => { }
})

// - Provider ---------------------

const NavigationProvider = () => {
    const [router, setRouter] = useState<ReturnType<typeof createBrowserRouter> | undefined>(undefined)
    const { information } = useAuthentication()
    const { getGenericStoreValue, setGenericStoreValue, removeGenericStoreValue } = useGenericStore()
    const { spaceRoutes, getAssistantByPath, getActiveAssistant, switchAssistant } = useSpace()

    const pendingNavigationRef = useRef<string | null>(null)
    const isFirstRouterRef = useRef(true)

    const spaceRoutesKey = spaceRoutes.map((r) => r.id ?? r.path ?? '').join(',')

    // Strip the Vite base path prefix from raw browser pathnames.
    const basePath = import.meta.env.BASE_URL.replace(/\/$/, '')

    const spacesLoader = async ({ request }: { request: Request }) => {
        const url = new URL(request.url)	
        const strippedPathname = basePath	
            ? url.pathname.replace(new RegExp(`^${basePath}`), '')	
            : url.pathname	
        const pathComponents = strippedPathname.split('/').filter(Boolean)	
        const assistant = getAssistantByPath(pathComponents[0], pathComponents[1])	

        const dataToRehydrate: LastChatInfo | undefined = getGenericStoreValue('dataToRehydrate')	
        const activeChatId = url.searchParams.get('chatId') || dataToRehydrate?.chatId	

        const assistantInitQueryParams = Object.fromEntries(	
            Array.from(url.searchParams.entries()).filter(([key]) => key !== 'chatId')	
        )	
        const hasAssistantInitQueryParams = Object.keys(assistantInitQueryParams).length > 0	

        if (hasAssistantInitQueryParams)	
            setGenericStoreValue('queryParams', assistantInitQueryParams, true)	
        else	
            removeGenericStoreValue('queryParams', true)	

        // `assistant.isActive` can be stale after switching spaces. Only skip when this is the assistant
        // actually active inside the currently active space.
        if (assistant && getActiveAssistant() !== assistant) {
            if (!hasAssistantInitQueryParams || !getGenericStoreValue('initialAssistantLoaded')) {	
                setGenericStoreValue('initialAssistantLoaded', true, false)	

                const switching = Promise.resolve(switchAssistant(assistant.config.id, activeChatId))
                    .catch((error) => console.error('[navigation] switchAssistant failed:', error))

                // Another assistant is active already: the route is shown at once (the layout shows the loading
                // indicator until the switch is done). The first load has none and waits for it.
                if (!getActiveAssistant()) await switching

            }	
        }	

        return null	
    }

    const defaultRoutes: RouteObject[] = [
        {
            id: 'identity',
            path: '/identity',
            element: <IdentityLayout key='identity-layout' />,
            children: [
                {
                    path: 'login',
                    element: <Login key='login' />,
                },
                {
                    path: 'unexpectedAuthenticationError',
                    element: <AuthenticationError key='auth-error' state={null} />,
                },
            ],
        },
        {
            id: 'root',
            path: '/',
            element: <BaseLayout key='base-layout' />,
            children: [
                {
                    index: true,
                    element: <Home key='home' />,
                },
                {
                    id: 'spaces',
                    loader: spacesLoader,
                    children: spaceRoutes.length === 0
                        ? [{ path: '/:spaceId/:assistantId', element: <ChatLoader /> }]
                        : [...spaceRoutes],
                },
            ],
        },
        {
            path: '/feedback',
            loader: async () => {
                return null
            },
            element: <BaseLayout key='base-layout-feedback' />,
            children: [
                {
                    index: true,
                    element: (
                        <ContentCenter>
                            <Feedback />
                        </ContentCenter>
                    ),
                },
            ],
        },
        {
            path: '/settings',
            loader: async () => {
                return null
            },
            element: <BaseLayout key='base-layout-settings' />,
            children: [
                {
                    index: true,
                    element: (
                        <ContentCenter>
                            <Settings information={information} />
                        </ContentCenter>
                    ),
                },
            ],
        },
        {
            path: '*',
            element: <IdentityLayout key='identity-layout-not-found' />,
            children: [
                {
                    path: '*',
                    element: <NotFound key='not-found' />,
                },
            ],
        },
    ]

    useEffect(() => {
        const strippedPathname = basePath
            ? window.location.pathname.replace(new RegExp(`^${basePath}`), '')
            : window.location.pathname
        const currentPath = strippedPathname + window.location.search

        if (!isFirstRouterRef.current && spaceRoutes.length > 0 && currentPath !== '/' && currentPath !== '') {
            pendingNavigationRef.current = currentPath
        }
        isFirstRouterRef.current = false

        const updatedRouter = createBrowserRouter(defaultRoutes, {
            basename: import.meta.env.BASE_URL
        })
        setRouter(updatedRouter)
        // the router is built again only when the routes change (the key lists their ids), not on every render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [spaceRoutesKey])

    useEffect(() => {
        if (!router || !pendingNavigationRef.current) return

        const pathToRestore = pendingNavigationRef.current
        pendingNavigationRef.current = null

        const timeout = setTimeout(() => {
            router.navigate(pathToRestore, { replace: true })
        }, 0)

        return () => clearTimeout(timeout)
    }, [router])

    const navigateTo = (path: string, options?: NavigateOptions) => {
        if (router)
            router.navigate(path, options)
    }

    return (
        <NavigationContext.Provider value={{ router, navigateTo }}>
            {
                router &&
                <RouterProvider router={router} future={{ v7_startTransition: true }} />
            }
            <StartupGate />
        </NavigationContext.Provider>
    )
}

export { NavigationProvider, NavigationContext }
